import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

import config from "@/config";
import { PatientCasesView } from "@/components/patient-cases-view";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import type { PatientCasesApiResponse } from "@/types/patient-case-api";

export interface PatientCasesJsonSheetTarget {
  patient_id: string;
  patient_name: string | null;
  j_code: string;
  date: string;
}

type Props = {
  target: PatientCasesJsonSheetTarget | null;
  onClose: () => void;
};

function buildVobReadyGroupCasesUrl(target: PatientCasesJsonSheetTarget): string {
  const params = new URLSearchParams();
  params.set("patient_id", target.patient_id);
  params.set("j_code", target.j_code);
  params.set("date", target.date);
  params.append("include", "stedi_eligibility_response");
  return `${config.API_BASE_URL}/api/dashboard/vob-ready-group-cases?${params.toString()}`;
}

async function fetchPatientCases(
  target: PatientCasesJsonSheetTarget,
): Promise<PatientCasesApiResponse> {
  const res = await fetch(buildVobReadyGroupCasesUrl(target));
  if (!res.ok) {
    let detail = `Request failed (${res.status})`;
    try {
      const data = (await res.json()) as { detail?: unknown };
      if (typeof data.detail === "string" && data.detail.trim()) {
        detail = data.detail;
      }
    } catch {
      // ignore non-JSON body
    }
    throw new Error(detail);
  }
  return (await res.json()) as PatientCasesApiResponse;
}

export function PatientCasesJsonSheet({ target, onClose }: Props) {
  const [response, setResponse] = useState<PatientCasesApiResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!target) {
      setResponse(null);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setResponse(null);

    void fetchPatientCases(target)
      .then((data) => {
        if (!cancelled) setResponse(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load patient cases");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [target, refreshKey]);

  return (
    <Sheet open={target != null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="flex h-full max-h-[100dvh] w-[min(92vw,1400px)] max-w-[92vw] flex-col overflow-hidden sm:max-w-[min(92vw,1400px)]">
        <SheetHeader>
          <SheetTitle>{target?.patient_name || "Patient cases"}</SheetTitle>
          <SheetDescription className="text-left">
            {target ? (
              <>
                <span className="font-mono">{target.patient_id}</span>
                {" · "}
                {target.j_code}
                {" · "}
                {target.date}
              </>
            ) : (
              "Case payloads for VOB generation."
            )}
          </SheetDescription>
        </SheetHeader>

        {loading ? (
          <div className="flex flex-1 items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading cases…
          </div>
        ) : error ? (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-6 text-sm text-destructive">
            {error}
          </div>
        ) : response ? (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <PatientCasesView
              response={response}
              onCasesRefresh={() => setRefreshKey((value) => value + 1)}
            />
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
